import React from 'react';
import { Link } from 'react-router-dom';
import { Home as HomeIcon, Building2, Briefcase, CheckCircle, Star, ArrowRight, Sparkles, ClipboardList, Snowflake, Wrench, Hammer, Shield, Package, RefreshCw, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services, testimonials } from '../data/mock';

const Home = () => {
  const iconMap = {
    home: HomeIcon,
    building: Building2,
    briefcase: Briefcase,
    clipboardList: ClipboardList,
    snowflake: Snowflake,
    wrench: Wrench,
    hammer: Hammer,
    shield: Shield,
    package: Package,
    refresh: RefreshCw,
    truck: Truck,
    sparkles: Sparkles
  };

  // Show featured services on home page - Property Caretaking first
  const featuredServices = [
    services[0],  // Property Caretaking
    services[1],  // Commercial Cleaning
    services[2],  // Residential Cleaning
    services[3],  // Office Cleaning
    services[4],  // Delivery Services
    services[5],  // Property Management
    services[7],  // Maintenance Services
    services[8]   // Snow Removal
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-amber-50 py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center space-x-2 bg-brand-gold/20 px-4 py-2 rounded-full border border-brand-gold/30">
                <Sparkles className="h-4 w-4 text-brand-gold sparkle-icon" />
                <span className="text-sm font-semibold text-brand-navy">Professional Property Care Services</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-brand-navy leading-tight">
                Sparkling Clean Spaces in
                <span className="text-brand-gold gold-shine"> Winnipeg</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-4">
                At Sparkle Property Care Ltd, we deliver comprehensive property care solutions for residential, commercial, and industrial properties across Manitoba. We proudly serve homeowners, businesses, property managers, landlords, property management companies, and Airbnb hosts with reliable, professional services tailored to their needs.
              </p>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-4">
                Our expertise includes property caretaking, commercial and office cleaning, property management, Airbnb cleaning, pre- and post-construction cleaning, residential and carpet cleaning, snow removal, building maintenance, warehouse cleaning, window cleaning, painting, roofing, restoration, move-in and move-out services, delivery services, waste management and garbage disposal, and property security, providing everything you need to keep your property clean, safe, secure, and operating at its best.
              </p>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                Driven by quality, reliability, and exceptional customer service, we are committed to protecting your investment and exceeding your expectations. Whether you need a one-time service or a customized ongoing maintenance plan, Sparkle Property Care Ltd is your trusted partner for complete property care across Manitoba.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/book">
                  <Button size="lg" className="bg-brand-navy hover:bg-brand-navy-dark text-white text-lg px-8 py-6 w-full sm:w-auto shadow-xl hover:shadow-2xl transition-all duration-300">
                    Get Free Quote
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a href="tel:2042946802">
                  <Button size="lg" variant="outline" className="border-2 border-brand-gold text-brand-navy hover:bg-brand-gold hover:text-white text-lg px-8 py-6 w-full sm:w-auto transition-all duration-300">
                    Call 204-294-6802
                  </Button>
                </a>
              </div>
              <div className="flex items-center space-x-8 pt-4">
                <div className="flex items-center space-x-2">
                  <div className="flex -space-x-2">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-blue to-brand-navy border-2 border-white shadow-md"></div>
                    ))}
                  </div>
                  <div className="text-sm">
                    <p className="font-bold text-brand-navy">500+ Happy Clients</p>
                    <div className="flex items-center text-brand-gold">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star key={i} className="h-3 w-3 fill-current" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl border-4 border-brand-gold/30">
                <img
                  src="https://images.unsplash.com/photo-1740657254989-42fe9c3b8cce?w=800&h=600&fit=crop"
                  alt="Professional diverse cleaning team"
                  className="w-full h-[500px] object-cover object-center"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-72 h-72 bg-brand-gold/20 rounded-full blur-3xl"></div>
              <div className="absolute -top-6 -left-6 w-72 h-72 bg-brand-blue/20 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive property care solutions for every need in Winnipeg, Manitoba
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredServices.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card key={service.id} className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-brand-gold card-hover bg-white">
                  <CardContent className="p-8 space-y-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-brand-blue/20 to-brand-navy/20 rounded-xl flex items-center justify-center group-hover:scale-110 group-hover:bg-brand-gold/20 transition-all duration-300 border-2 border-brand-blue/30 group-hover:border-brand-gold">
                      <Icon className="h-7 w-7 text-brand-navy group-hover:text-brand-gold transition-colors duration-300" />
                    </div>
                    <h3 className="text-xl font-bold text-brand-navy group-hover:text-brand-gold transition-colors duration-300">{service.title}</h3>
                    <p className="text-gray-600">{service.shortDescription}</p>
                    <ul className="space-y-2">
                      {service.features.slice(0, 3).map((feature, idx) => (
                        <li key={`${service.id}-home-feature-${idx}`} className="flex items-start space-x-2 text-sm text-gray-600">
                          <CheckCircle className="h-4 w-4 text-brand-blue mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to="/services">
                      <Button variant="ghost" className="text-brand-navy hover:text-brand-gold hover:bg-brand-gold/10 p-0 h-auto font-semibold transition-colors duration-300">
                        Learn More <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Sparkle Property Care Ltd?
            </h2>
            <p className="text-lg text-gray-600">
              Trusted cleaning partner for Winnipeg residents and businesses
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: 'Professional Team', description: 'Trained and experienced cleaning professionals' },
              { title: 'Flexible Scheduling', description: 'We work around your schedule' },
              { title: 'Eco-Friendly Products', description: 'Safe for your family and pets' },
              { title: 'Satisfaction Guaranteed', description: '100% satisfaction or we return' }
            ].map((item) => (
              <div key={item.title} className="text-center space-y-3 group">
                <div className="w-16 h-16 bg-brand-navy rounded-full flex items-center justify-center mx-auto group-hover:bg-brand-gold transition-all duration-300 shadow-lg">
                  <CheckCircle className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-brand-navy">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-lg text-gray-600">
              Serving Winnipeg since October 2025
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.slice(0, 3).map((testimonial) => (
              <Card key={testimonial.id} className="border-2 hover:border-brand-gold transition-all duration-300 hover:shadow-xl">
                <CardContent className="p-6 space-y-4">
                  <div className="flex text-brand-gold">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={`${testimonial.id}-star-${i}`} className="h-5 w-5 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-700 italic">"{testimonial.text}"</p>
                  <div>
                    <p className="font-semibold text-brand-navy">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.location}</p>
                    <p className="text-xs text-brand-blue mt-1 font-medium">{testimonial.service}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-brand-navy via-brand-navy-light to-brand-blue relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready for a Spotless Space?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Get your free quote today and experience the Sparkle difference
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book">
              <Button size="lg" className="bg-brand-gold hover:bg-brand-gold-dark text-brand-navy font-bold text-lg px-8 py-6 w-full sm:w-auto shadow-2xl hover:shadow-3xl transition-all duration-300">
                Book Appointment
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a href="tel:2042946802">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-brand-navy text-lg px-8 py-6 w-full sm:w-auto transition-all duration-300">
                Call Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
